"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var _a, _b, _c, _d;
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
/* eslint-disable react/button-has-type */
const react_1 = require("react");
const classnames_1 = __importDefault(require("classnames"));
const constants_1 = require("../../constants");
const useResizeObserver_1 = __importDefault(require("../../hooks/useResizeObserver"));
const useMergeRefs_1 = __importDefault(require("../../hooks/useMergeRefs"));
const function_utils_1 = require("../../utils/function-utils");
const Icon_1 = __importDefault(require("../../components/Icon/Icon"));
const Loader_1 = __importDefault(require("../../components/Loader/Loader"));
const ButtonConstants_1 = require("./ButtonConstants");
const dom_helpers_1 = require("./helper/dom-helpers");
const test_utils_1 = require("../../utils/test-utils");
require("./Button.scss");
// @ts-ignore
const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
// min button width
const MIN_BUTTON_HEIGHT_PX = isIE11 ? 32 : 6;
const UPDATE_CSS_VARIABLES_DEBOUNCE = 200;
const Button = (0, react_1.forwardRef)(({ className, children, kind, onClick, name, size, color, disabled, rightIcon, leftIcon, success, successText, successIcon, style, loading, active, id, marginRight, marginLeft, type, onMouseDown, ariaLabel, rightFlat, leftFlat, preventClickAnimation, noSidePadding, onFocus, onBlur, ariaLabeledBy, defaultTextColorOnPrimaryColor, ariaHasPopup, ariaExpanded, ariaControls, blurOnMouseUp, dataTestId, insetFocus }, ref) => {
    const buttonRef = (0, react_1.useRef)(null);
    const [hasSizeStyle, setHasSizeStyle] = (0, react_1.useState)(false);
    const updateCssVariables = (0, react_1.useMemo)(() => {
        return ({ borderBoxSize }) => {
            const { blockSize, inlineSize } = borderBoxSize;
            const width = Math.max(inlineSize, MIN_BUTTON_HEIGHT_PX);
            const height = Math.max(blockSize, MIN_BUTTON_HEIGHT_PX);
            if (!buttonRef.current)
                return;
            buttonRef.current.style.setProperty("--element-width", `${width}px`);
            buttonRef.current.style.setProperty("--element-height", `${height}px`);
            setHasSizeStyle(true);
        };
    }, [buttonRef]);
    (0, useResizeObserver_1.default)({
        ref: buttonRef,
        callback: updateCssVariables,
        debounceTime: UPDATE_CSS_VARIABLES_DEBOUNCE
    });
    (0, react_1.useEffect)(() => {
        if (color !== ButtonConstants_1.ButtonColor.ON_PRIMARY_COLOR)
            return;
        if (kind !== ButtonConstants_1.ButtonType.PRIMARY)
            return;
        if (!buttonRef.current)
            return;
        const buttonElement = buttonRef.current;
        buttonElement.style.color = (0, dom_helpers_1.getParentBackgroundColorNotTransparent)(buttonElement, defaultTextColorOnPrimaryColor);
    }, [kind, buttonRef, color, defaultTextColorOnPrimaryColor]);
    const onMouseUp = (0, react_1.useCallback)(() => {
        const button = buttonRef.current;
        if (disabled || !button) {
            return;
        }
        if (blurOnMouseUp) {
            button.blur();
        }
    }, [disabled, buttonRef, blurOnMouseUp]);
    const onButtonClicked = (0, react_1.useCallback)((event) => {
        if (disabled || loading || success) {
            event.preventDefault();
            return;
        }
        if (onClick) {
            onClick(event);
        }
    }, [onClick, disabled, loading, success]);
    const onMouseDownClicked = (0, react_1.useCallback)((event) => {
        if (disabled || loading || success) {
            event.preventDefault();
            return;
        }
        if (onMouseDown) {
            onMouseDown(event);
        }
    }, [onMouseDown, disabled, loading, success]);
    const classNames = (0, react_1.useMemo)(() => {
        const calculatedColor = success ? ButtonConstants_1.ButtonColor.POSITIVE : color;
        return (0, classnames_1.default)(className, "monday-style-button", `monday-style-button--size-${(0, ButtonConstants_1.getActualSize)(size)}`, `monday-style-button--kind-${kind}`, `monday-style-button--color-${calculatedColor}`, {
            "has-style-size": hasSizeStyle,
            "monday-style-button--loading": loading,
            [`monday-style-button--color-${calculatedColor}-active`]: active,
            "monday-style-button--margin-right": marginRight,
            "monday-style-button--margin-left": marginLeft,
            "monday-style-button--right-flat": rightFlat,
            "monday-style-button--left-flat": leftFlat,
            "monday-style-button--prevent-click-animation": preventClickAnimation,
            "monday-style-button--no-side-padding": noSidePadding,
            "monday-style-button--disabled": disabled,
            "inset-focus-style": insetFocus
        });
    }, [
        success,
        color,
        className,
        size,
        kind,
        hasSizeStyle,
        loading,
        active,
        marginRight,
        marginLeft,
        rightFlat,
        leftFlat,
        preventClickAnimation,
        noSidePadding,
        disabled,
        insetFocus
    ]);
    const mergedRef = (0, useMergeRefs_1.default)({ refs: [ref, buttonRef] });
    const buttonProps = (0, react_1.useMemo)(() => {
        const props = {
            ref: mergedRef,
            type,
            className: classNames,
            name,
            onMouseUp,
            style,
            onClick: onButtonClicked,
            id,
            onFocus,
            onBlur,
            "data-testid": dataTestId || (0, test_utils_1.getTestId)(test_utils_1.ELEMENT_TYPES.BUTTON, id),
            onMouseDown: onMouseDownClicked,
            "aria-disabled": disabled,
            "aria-busy": loading,
            "aria-labelledby": ariaLabeledBy,
            "aria-label": ariaLabel,
            "aria-haspopup": ariaHasPopup,
            "aria-expanded": ariaExpanded,
            "aria-controls": ariaControls
        };
        return props;
    }, [
        disabled,
        mergedRef,
        type,
        classNames,
        name,
        onMouseUp,
        style,
        onButtonClicked,
        id,
        onFocus,
        onBlur,
        dataTestId,
        onMouseDownClicked,
        ariaLabeledBy,
        ariaLabel,
        loading,
        ariaHasPopup,
        ariaExpanded,
        ariaControls
    ]);
    const leftIconSize = (0, react_1.useMemo)(() => {
        if (typeof leftIcon !== "function")
            return;
        return ButtonConstants_1.BUTTON_ICON_SIZE;
    }, [leftIcon]);
    const rightIconSize = (0, react_1.useMemo)(() => {
        if (typeof rightIcon !== "function")
            return;
        return ButtonConstants_1.BUTTON_ICON_SIZE;
    }, [rightIcon]);
    const successIconSize = (0, react_1.useMemo)(() => {
        if (typeof successIcon !== "function")
            return;
        return ButtonConstants_1.BUTTON_ICON_SIZE;
    }, [successIcon]);
    if (loading) {
        return ((0, jsx_runtime_1.jsx)("button", Object.assign({}, buttonProps, { children: (0, jsx_runtime_1.jsx)("span", Object.assign({ className: "monday-style-button__loader" }, { children: (0, jsx_runtime_1.jsx)(Loader_1.default, { svgClassName: "monday-style-button-loader-svg" }) })) })));
    }
    if (success) {
        return ((0, jsx_runtime_1.jsxs)("button", Object.assign({}, buttonProps, { children: [successIcon ? ((0, jsx_runtime_1.jsx)(Icon_1.default, { iconType: Icon_1.default === null || Icon_1.default === void 0 ? void 0 : Icon_1.default.type.ICON_FONT, clickable: false, icon: successIcon, iconSize: successIconSize, className: (0, classnames_1.default)({
                        "monday-style-button--left-icon": !!successText
                    }), ignoreFocusStyle: true })) : null, successText] })));
    }
    return ((0, jsx_runtime_1.jsxs)("button", Object.assign({}, buttonProps, { children: [leftIcon ? ((0, jsx_runtime_1.jsx)(Icon_1.default, { iconType: Icon_1.default === null || Icon_1.default === void 0 ? void 0 : Icon_1.default.type.ICON_FONT, clickable: false, icon: leftIcon, iconSize: leftIconSize, className: (0, classnames_1.default)({ "monday-style-button--left-icon": !!children }), ignoreFocusStyle: true })) : null, children, rightIcon ? ((0, jsx_runtime_1.jsx)(Icon_1.default, { iconType: Icon_1.default === null || Icon_1.default === void 0 ? void 0 : Icon_1.default.type.ICON_FONT, clickable: false, icon: rightIcon, iconSize: rightIconSize, className: (0, classnames_1.default)({ "monday-style-button--right-icon": !!children }), ignoreFocusStyle: true })) : null] })));
});
Object.assign(Button, {
    sizes: constants_1.SIZES,
    colors: ButtonConstants_1.ButtonColor,
    kinds: ButtonConstants_1.ButtonType,
    types: ButtonConstants_1.ButtonInputType,
    inputTags: ButtonConstants_1.ButtonInputType
});
Button.defaultProps = {
    className: undefined,
    name: undefined,
    style: undefined,
    id: undefined,
    dataTestId: undefined,
    kind: (_a = Button.kinds) === null || _a === void 0 ? void 0 : _a.PRIMARY,
    onClick: function_utils_1.NOOP,
    size: (_b = Button.sizes) === null || _b === void 0 ? void 0 : _b.MEDIUM,
    color: (_c = Button.colors) === null || _c === void 0 ? void 0 : _c.PRIMARY,
    disabled: false,
    rightIcon: null,
    leftIcon: null,
    success: false,
    successText: "",
    successIcon: null,
    loading: false,
    active: false,
    marginRight: false,
    marginLeft: false,
    type: (_d = Button.types) === null || _d === void 0 ? void 0 : _d.BUTTON,
    onMouseDown: function_utils_1.NOOP,
    rightFlat: false,
    leftFlat: false,
    preventClickAnimation: false,
    noSidePadding: false,
    onFocus: function_utils_1.NOOP,
    onBlur: function_utils_1.NOOP,
    defaultTextColorOnPrimaryColor: dom_helpers_1.TRANSPARENT_COLOR,
    ariaHasPopup: undefined,
    blurOnMouseUp: true,
    ariaExpanded: undefined,
    ariaControls: undefined,
    ariaLabel: undefined,
    ariaLabeledBy: undefined,
    insetFocus: false
};
exports.default = Button;
